// Derives the portal palette from the tenant's brand color using the SAME algorithm the
// Flutter app uses: ColorScheme.fromSeed(seedColor, dynamicSchemeVariant: fidelity).
// See buildMobileScheme.ts for the provenance and the parity test.
//
// BEHAVIOUR CHANGE (was: HSL hue+saturation kept, lightness forced to 0.32, then
// lighten/darken). The old derivation deliberately discarded the configured shade so that
// white-on-primary stayed legible. Material 3 solves the same problem differently: it keeps
// the configured colour as `primaryContainer`, derives `primary` at a tone that meets
// contrast, and computes `onPrimary` rather than guessing black or white. The consequence
// worth knowing: two shades of the same hue no longer collapse to one base — that was the
// point of the old code and it is not how the phone behaves.
import {
  buildMobileScheme,
  MobileScheme,
} from './buildMobileScheme'
import { Hct, MaterialDynamicColors, SchemeFidelity, argbFromHex, hexFromArgb } from '../../vendor/material-color-utilities'

export interface PrimaryPaletteTones {
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string // base/main — the exact `primary` the Flutter app renders
  600: string // dark
  700: string
  800: string
  900: string
}

export interface PrimaryPalette {
  main: string
  light: string
  dark: string
  contrastText: string
  tones: PrimaryPaletteTones
  /** Every generated Material role, for the parts of the theme beyond primary. */
  scheme: MobileScheme
}

/**
 * Tone offsets around the generated primary.
 *
 * Material has no 50..900 ramp — this is the one number set that had to be chosen rather
 * than copied, so it lives here and nowhere else. Anchoring to the primary's own tone
 * (rather than fixed tones) keeps step 500 exactly equal to what the phone renders while
 * guaranteeing the ramp stays ordered for any brand colour, including very dark ones.
 */
const TONE_OFFSET_UP: Record<number, number> = { 50: 55, 100: 50, 200: 40, 300: 30, 400: 20 }
const TONE_OFFSET_DOWN: Record<number, number> = { 600: 10, 700: 15, 800: 20, 900: 30 }

export const buildPrimaryPalette = (inputColor: string): PrimaryPalette => {
  const scheme = buildMobileScheme(inputColor, false)

  // Same construction as buildMobileScheme, reused here for the tonal palette.
  const dynamic = new SchemeFidelity(Hct.fromInt(argbFromHex(inputColor)), false, 0)
  const primaryTone = Hct.fromInt(argbFromHex(scheme.primary)).tone

  // Compress the offsets when the primary sits near either end, so the ramp never
  // flattens into a run of identical blacks or whites.
  const headroomUp = Math.min(55, 100 - primaryTone) / 55
  const headroomDown = Math.min(30, primaryTone) / 30
  const at = (tone: number) => hexFromArgb(dynamic.primaryPalette.tone(tone)).toLowerCase()

  const tones = { 500: scheme.primary } as PrimaryPaletteTones
  ;(Object.keys(TONE_OFFSET_UP) as unknown as Array<keyof PrimaryPaletteTones>).forEach((step) => {
    tones[step] = at(primaryTone + TONE_OFFSET_UP[step as unknown as number] * headroomUp)
  })
  ;(Object.keys(TONE_OFFSET_DOWN) as unknown as Array<keyof PrimaryPaletteTones>).forEach((step) => {
    tones[step] = at(primaryTone - TONE_OFFSET_DOWN[step as unknown as number] * headroomDown)
  })

  return {
    main: tones[500],
    light: tones[400],
    dark: tones[600],
    // Not a black/white guess: this is the scheme's own onPrimary, which is what the phone
    // paints on top of primary.
    contrastText: scheme.onPrimary,
    tones,
    scheme,
  }
}
